import { getAdminOrgId } from "./admin";
import { getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type NoteType = "general" | "call" | "meeting" | "complaint" | "other";

export type Note = {
  id: string;
  entity_type: string;
  entity_id: string;
  note_type: NoteType;
  content: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  created_by_user?: {
    id: string;
    email: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
  };
};

export type NoteCreate = {
  entity_type: string;
  entity_id: string;
  note_type: NoteType;
  content: string;
};

export type NoteUpdate = {
  note_type?: NoteType;
  content?: string;
};

export type PagedNotes = {
  items: Note[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  };
};

export async function listNotes(params: {
  entity_type?: string;
  entity_id?: string;
  note_type?: NoteType;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<PagedNotes> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params.entity_type) queryParams.append("entity_type", params.entity_type);
  if (params.entity_id) queryParams.append("entity_id", params.entity_id);
  if (params.note_type) queryParams.append("note_type", params.note_type);
  if (params.search) queryParams.append("search", params.search);
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.page_size) queryParams.append("page_size", params.page_size.toString());

  const response = await fetch(`${API_BASE}/notes?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load notes");
  }
  return response.json();
}

export async function createNote(payload: NoteCreate): Promise<Note> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/notes`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to create note");
  }
  return response.json();
}

export async function updateNote(noteId: string, payload: NoteUpdate): Promise<Note> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/notes/${noteId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update note");
  }
  return response.json();
}

export async function deleteNote(noteId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/notes/${noteId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to delete note");
  }
}
